<template>
  <ProjectNodeLayout :content-size="Size.Tiny">
    <template #content>
      <Section v-if="node.powerState === ServerPowerState.ON" :size="Size.Tiny" title="Power off">
        <p>
          When you turn off the node, a graceful shutdown will be executed. If that graceful
          shutdown fails, we force the shutdown, which may corrupt data your data. To ensure data
          integrity, you should shutting down the server via command line.
        </p>

        <GpBtn
          class="tw-mt-8 tw-w-full"
          btn-type="warning"
          :icon="['fas', 'fa-power-off']"
          label="Power off"
          :disabled="!nodeActionsAllowed"
          @click="showPowerOffConfirm = true"
        />
      </Section>

      <Section v-if="node.powerState === ServerPowerState.OFF" :size="Size.Tiny" title="Power on">
        <GpBtn
          class="tw-mt-8 tw-w-full"
          :icon="['fas', 'fa-play']"
          label="Power on"
          :disabled="!nodeActionsAllowed"
          @click="showPowerOnConfirm = true"
        />
      </Section>

      <Section :size="Size.Tiny" title="Power reset">
        <p class="tw-mt-4">
          A power reset will immediately reset the server (as if you were to pull the mains plug for
          a shot time). You should only choose this option when you are unable to reboot the node
          the node from the command line.
        </p>

        <p class="tw-mt-4">
          <strong class="tw-text-gp-fire">Warning</strong>: This may cause data loss or damage your
          file system.
        </p>

        <GpBtn
          class="tw-mt-8 tw-w-full"
          btn-type="warning"
          label="Power reset"
          :icon="['fas', 'fa-plug']"
          :disabled="!nodeActionsAllowed"
          @click="showPowerResetConfirm = true"
        />
      </Section>

      <ConfirmModal
        :show="showPowerOffConfirm"
        confirm-type="warning"
        confirm-text="Power off"
        confirm-icon="power-off"
        @confirm="onConfirmPowerOff"
        @close="showPowerOffConfirm = false"
      />

      <ConfirmModal
        :show="showPowerOnConfirm"
        confirm-type="primary"
        confirm-text="Power on"
        confirm-icon="play"
        @confirm="onConfirmPowerOn"
        @close="showPowerOnConfirm = false"
      />

      <ConfirmModal
        :show="showPowerResetConfirm"
        confirm-type="warning"
        confirm-text="Power reset"
        confirm-icon="plug"
        @confirm="onConfirmPowerReset"
        @close="showPowerResetConfirm = false"
      />
    </template>
  </ProjectNodeLayout>
</template>

<script lang="ts">
import { GpBtn } from '@gportal/uikit';
import useViewTitle from '@panel/composables/useViewTitle';
import ProjectNodeLayout from '@panel/layouts/Layout.Project.Node.vue';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import usePowerAction from 'gpcore-apikit/store/pinia/features/node/usePowerAction';
import { useNodeStore } from 'gpcore-apikit/store/pinia/node';
import { ServerPowerState } from 'gpcore-grpc/gpcore/api/cloud/v1/server_pb';
import Section from 'gpcore-uikit/components/Component.Section.vue';
import ConfirmModal from 'gpcore-uikit/components/new/ConfirmModal.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import Size from 'gpcore-uikit/interfaces/Size';
import { mapState } from 'pinia';
import { defineComponent } from 'vue';

export default defineComponent({
  components: {
    ProjectNodeLayout,
    Section,
    GpBtn,
    ConfirmModal,
  },
  data() {
    return {
      Size,
      showPowerOffConfirm: false,
      showPowerOnConfirm: false,
      showPowerResetConfirm: false,
      ServerPowerState,
    };
  },
  computed: {
    ...mapState(useNodeStore, ['nodeActionsAllowed', 'node']),
  },
  methods: {
    togglePowerOffConfirm() {
      this.showPowerOffConfirm = !this.showPowerOffConfirm;
    },
    onConfirmPowerOn() {
      this.powerAction({
        node: this.node,
        action: ServerPowerState.ON,
      })
        .then(() => {
          this.addNotification({
            message: 'The power on action will be executed now.',
            type: 'success',
          });
        })
        .catch((error: ServiceError) => {
          this.addNotification({ message: error.message, type: 'danger' });
        })
        .finally(() => {
          this.showPowerOnConfirm = false;
        });
    },
    onConfirmPowerOff() {
      this.powerAction({
        node: this.node,
        action: ServerPowerState.OFF,
      })
        .then(() => {
          this.addNotification({
            message: 'The power off action will be executed now.',
            type: 'success',
          });
        })
        .catch((error: ServiceError) => {
          this.addNotification({ message: error.message, type: 'danger' });
        })
        .finally(() => {
          this.showPowerOffConfirm = false;
        });
    },
    onConfirmPowerReset() {
      this.powerAction({
        node: this.node,
        action: ServerPowerState.RESET,
      })
        .then(() => {
          this.addNotification({
            message: 'The power reset action will be executed now.',
            type: 'success',
          });
        })
        .catch((error: ServiceError) => {
          this.addNotification({ message: error.message, type: 'danger' });
        })
        .finally(() => {
          this.showPowerResetConfirm = false;
        });
    },
  },
  setup() {
    useViewTitle('Node power');

    const powerAction = usePowerAction();

    const { addNotification } = useNotifications();

    return {
      powerAction,
      addNotification,
    };
  },
});
</script>
